import React, { useEffect, useState, useSyncExternalStore } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  Button,
  Checkbox,
  FormControlLabel,
  Tooltip,
  TextField,
  IconButton,
} from "@mui/material";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import SendIcon from "@mui/icons-material/Send";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { primaryColor } from "../../../Theme";
import { getPatientId } from "../../../utils/patientSession";
import { setPrescriptionFlag } from "../../../utils/AIPrescription/prescriptionFlagsStore";
import { markSendOriginButton } from "../../../utils/AIPrescription/sendOriginStore";
import {
  getSessionBenefitSource,
  isResolvedBenefitSource,
  subscribeSessionBenefitSource,
} from "../../../utils/AIPrescription/sessionBenefitSourceStore";
import {
  setPrescriptionEdits,
  getPrescriptionEdits,
  getRejectedFields,
  mergeFreshBaselineWithStaleEdits,
  dropRejectedEdits,
  FIELD_PHRASE,
  PrescriptionEdits,
} from "../../../utils/AIPrescription/prescriptionEditsStore";

const SUBSTITUTION_OPTIONS = [
  "Substitutions allowed",
  "Dispense as written",
];

const DEFAULT_SUBSTITUTION = SUBSTITUTION_OPTIONS[0];

// Medication name heading size — slightly smaller than the original mockup.
const DRUG_NAME_FONT_SIZE = "1.15rem";

// MUI TextField / InputBase ship with min-height and vertical padding that make edit
// rows taller than the matching Typography rows. Reset so view ↔ edit stay the same size.
const INPUT_SIZE_RESET_SX = {
  "& .MuiInputBase-root": {
    minHeight: "unset",
    height: "auto",
    py: 0,
    my: 0,
  },
  "& .MuiInputBase-input": {
    minHeight: "unset",
    height: "auto",
    py: 0,
    my: 0,
    boxSizing: "content-box",
  },
} as const;

// Bold field labels — same weight/color as EPA / Priority Indicator labels.
const FIELD_LABEL_SX = {
  fontWeight: 600,
  whiteSpace: "nowrap",
} as const;

const FIELD_VALUE_SX = {
  fontWeight: 400,
  color: "#37474f",
} as const;

// Approximate input width from value length. `ch` is the width of "0", which is
// narrower than capitals (e.g. "MCG"), so we add headroom and prefer
// `field-sizing: content` where the browser supports it.
const valueInputWidth = (value: string, minChars = 3) =>
  `calc(${Math.max(String(value).length + 2, minChars)}ch + 12px)`;

// Shared edit-mode look: bordered + light tint so editable fields are obvious.
const editableFieldRootSx = {
  fontSize: "0.875rem",
  fontWeight: 400,
  color: "#37474f",
  lineHeight: 1.5,
  backgroundColor: `${primaryColor}0d`,
  border: `1px solid ${primaryColor}55`,
  borderRadius: "6px",
  transition: "border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease",
  "&:hover": {
    borderColor: primaryColor,
    backgroundColor: `${primaryColor}14`,
  },
  "&.Mui-focused": {
    borderColor: primaryColor,
    backgroundColor: "#ffffff",
    boxShadow: `0 0 0 2px ${primaryColor}33`,
  },
} as const;

// Inline TextField — sits on the same line as its label (no block-level wrap).
const inlineTextFieldSx = (value: string) =>
  ({
    display: "inline-flex",
    width: "auto",
    maxWidth: "100%",
    verticalAlign: "middle",
    ...INPUT_SIZE_RESET_SX,
    "& .MuiInputBase-root": {
      ...INPUT_SIZE_RESET_SX["& .MuiInputBase-root"],
      ...editableFieldRootSx,
      display: "inline-flex",
      width: "auto",
      maxWidth: "100%",
      px: 0.75,
      py: 0.35,
    },
    "& .MuiInputBase-input": {
      ...INPUT_SIZE_RESET_SX["& .MuiInputBase-input"],
      display: "block",
      padding: "1px 2px",
      width: valueInputWidth(value),
      minWidth: "3ch",
      maxWidth: "100%",
      fieldSizing: "content",
      cursor: "text",
      lineHeight: 1.5,
    },
  }) as const;

const REFILLS_INPUT_SX = {
  display: "inline-flex",
  width: "auto",
  verticalAlign: "middle",
  ...INPUT_SIZE_RESET_SX,
  "& .MuiInputBase-root": {
    ...INPUT_SIZE_RESET_SX["& .MuiInputBase-root"],
    ...editableFieldRootSx,
    display: "inline-flex",
    width: "auto",
    alignItems: "center",
    px: 0.75,
    py: 0.35,
  },
  "& .MuiInputBase-input": {
    ...INPUT_SIZE_RESET_SX["& .MuiInputBase-input"],
    padding: "1px 2px",
    width: "4ch",
    textAlign: "left",
    lineHeight: 1.5,
  },
} as const;

// SIG textarea sits beside the "SIG:" label via flexbox (see renderInlineEditField),
// so it grows on the same first line as the label instead of wrapping under it.
const SIG_INPUT_SX = {
  m: 0,
  ...INPUT_SIZE_RESET_SX,
  "& .MuiInputBase-root": {
    ...INPUT_SIZE_RESET_SX["& .MuiInputBase-root"],
    ...editableFieldRootSx,
    display: "block",
    width: "100%",
    px: 0.75,
    py: 0.5,
  },
  "& textarea.MuiInputBase-input": {
    ...INPUT_SIZE_RESET_SX["& .MuiInputBase-input"],
    display: "block",
    padding: "1px 2px",
    lineHeight: 1.5,
    overflow: "hidden",
    resize: "none",
    width: "100%",
    boxSizing: "border-box",
  },
} as const;

interface Prescription {
  id?: number;
  medication_name?: string;
  drugName?: string;
  dosage?: string;
  route_of_administration?: string;
  dosage_form?: string;
  qualifier_types?: string;
  sig?: string;
  quantity?: string;
  substitutions?: string;
  pharmacy?: string;
  refills?: number;
  // drugPopulate-derived fields surfaced on the summary card.
  epaFlag?: boolean;
  priorAuthorization?: boolean;
  priorityIndicator?: boolean;
  coverage?: string;
  scheduleDrugFlag?: boolean;
  deaClassCode?: string;
  epcsQueued?: boolean;
  // Already sent to a pharmacy this session — shown read-only (no Send button).
  sent?: boolean;
}

// Coverage is shown only when it begins with this phrase; we display just the phrase.
const PA_PHRASE = "Prior Authorization required";
const coverageDisplay = (c?: string): string | null =>
  typeof c === "string" && c.trim().toLowerCase().startsWith(PA_PHRASE.toLowerCase())
    ? PA_PHRASE
    : null;

const flagKeyOf = (p: Prescription): string =>
  ((p.medication_name || p.drugName || "").trim()).replace(/\s+/g, " ").toLowerCase();

interface PrescriptionSummaryProps {
  prescriptions: Prescription[];
  conversationId: number;
  preferredPharmacy?: string;
  // Submits a chat message that triggers the send2Pharmacy flow.
  // Phrasing must match the orchestration agent's send recognizer
  // (message_indicates_send_to_pharmacy + resolve_send_scope_from_message).
  onSendPrescription?: (message: string) => void;
  /** True while a previous turn (including a prior Send/Send all click) is still
   *  in flight. Disables Send/Send all so a double-click can't submit the same
   *  prescription to the pharmacy twice — mirrors the chat input's isSearching gate. */
  disabled?: boolean;
}

// Build the exact chat message the send recognizer understands.
// Single med -> ("single", [that drug]) via the med-scoped send regex.
const buildSendPrescriptionMessage = (medicationName: string): string =>
  `send ${medicationName.trim()} prescription to pharmacy`;

// "all" in the message -> ("batch", [all ready prescriptions]).
const SEND_ALL_PRESCRIPTIONS_MESSAGE = "send all prescriptions to pharmacy";

const PrescriptionSummary: React.FC<PrescriptionSummaryProps> = ({
  prescriptions,
  conversationId,
  preferredPharmacy = "",
  onSendPrescription,
  disabled = false,
}) => {
  const canSend = typeof onSendPrescription === "function";
  // Already-sent prescriptions are read-only; only unsent ones count toward "Send all".
  const sendableCount = prescriptions.filter((p) => !p.sent).length;
  const showSendAll = canSend && sendableCount > 1;

  // EPA / Priority Indicator / Coverage: getUserServiceLevels.epaEnabled AND a resolved
  // session PBM (not SELECT_BENEFIT_SOURCE). EPCS also needs epcsenabled + scheduleDrugFlag.
  // Benefit source is session-level so a later PBM pick re-renders already-mounted cards.
  const epaEnabled = useSelector(
    (state: any) => state?.UserServicesSlice?.ServicesData?.epaEnabled === true
  );
  const epcsEnabled = useSelector(
    (state: any) => state?.UserServicesSlice?.ServicesData?.epcsenabled === true
  );
  const patientId = getPatientId();
  const sessionRxhub = useSyncExternalStore(
    subscribeSessionBenefitSource,
    () => getSessionBenefitSource(patientId)
  );
  const hasResolvedBenefitSource = isResolvedBenefitSource(sessionRxhub);
  const showEpaPriority = epaEnabled && hasResolvedBenefitSource;

  // Per-prescription EPA / Priority Indicator checkbox state (keyed by medication name).
  // EPA seeds from the drugPopulate ePAFlag; Priority Indicator always starts unchecked.
  const [flagState, setFlagState] = useState<
    Record<
      string,
      { priorAuthorization: boolean; priorityIndicator: boolean; epcsQueued: boolean }
    >
  >({});

  const defaultFlags = (p: Prescription) => ({
    priorAuthorization: p.epaFlag === true,
    priorityIndicator: false,
    epcsQueued: p.epcsQueued === true,
  });
  const effectiveFlags = (p: Prescription) => flagState[flagKeyOf(p)] ?? defaultFlags(p);
  const showEpcsCheckbox = (p: Prescription) =>
    !p.sent &&
    epcsEnabled &&
    p.scheduleDrugFlag === true &&
    hasResolvedBenefitSource;

  // Mirror the effective selections into the ambient store so createPayload sends them
  // (including the EPA default when the clinician never toggles anything).
  useEffect(() => {
    prescriptions.forEach((p) => {
      const name = (p.medication_name || p.drugName || "").trim();
      if (!name) return;
      // Hidden controls must not leak flags into the send (no EPA service and/or no real PBM).
      setPrescriptionFlag(
        patientId,
        name,
        {
          priorAuthorization: showEpaPriority
            ? effectiveFlags(p).priorAuthorization
            : false,
          priorityIndicator: showEpaPriority
            ? effectiveFlags(p).priorityIndicator
            : false,
          epcsQueued: showEpcsCheckbox(p) ? effectiveFlags(p).epcsQueued : false,
        }
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prescriptions, flagState, patientId, showEpaPriority, epcsEnabled, hasResolvedBenefitSource]);

  const handleFlagToggle = (
    p: Prescription,
    field: "priorAuthorization" | "priorityIndicator" | "epcsQueued",
    checked: boolean
  ) => {
    const k = flagKeyOf(p);
    setFlagState((prev) => {
      const cur = prev[k] ?? defaultFlags(p);
      return { ...prev, [k]: { ...cur, [field]: checked } };
    });
  };

  // Per-prescription editable field values (keyed by medication name). Seeded lazily
  // from the prescription; this component is mounted per conversation card so edits
  // never bleed across conversations. Edits are mirrored to the ambient edits store so
  // the Send handler can build an update-then-send instruction.
  const [editState, setEditState] = useState<Record<string, PrescriptionEdits>>({});

  // The server-baseline values (see defaultEdits below) captured at the moment editState[k]
  // was first populated. Edits are keyed only by drug name (flagKeyOf), so without this a
  // stale edit made against one resolved strength/drugId would silently keep overriding
  // every later card for that same drug name even after the draft's own catalog match moved
  // on to a different strength/drugId in a later turn -- regardless of what the server now
  // says. effectiveEdits below compares this against the CURRENT baseline every render and
  // ignores editState[k] once they diverge.
  const [editBaseline, setEditBaseline] = useState<Record<string, PrescriptionEdits>>({});

  // Per-prescription edit mode — pencil icon toggles inline editing for that card.
  const [editModeState, setEditModeState] = useState<Record<string, boolean>>({});

  const isEditing = (p: Prescription): boolean =>
    !!editModeState[flagKeyOf(p)];

  const toggleEditMode = (p: Prescription) => {
    const k = flagKeyOf(p);
    setEditModeState((prev) => ({ ...prev, [k]: !prev[k] }));
  };

  const defaultEdits = (p: Prescription): PrescriptionEdits => ({
    drugName: (p.medication_name || p.drugName || "").toString(),
    dosage: (p.dosage || "").toString(),
    route: (p.route_of_administration || "").toString(),
    form: (p.dosage_form ?? p.qualifier_types ?? "").toString(),
    sig: (p.sig || "").toString(),
    quantity: (p.quantity || "").toString(),
    refills: (p.refills ?? 0).toString(),
    substitutions: (p.substitutions || "").toString(),
    pharmacy: (p.pharmacy?.trim() || preferredPharmacy || "").toString(),
  });

  // Local edits win while this card instance is mounted — but per FIELD, not as one
  // all-or-nothing bundle: a field is only kept from editState if it was actually touched
  // (differs from the baseline captured when the edit was made) AND that field's own
  // server baseline hasn't moved on since. Comparing the whole snapshot for equality would
  // discard a still-valid SIG edit just because an unrelated field (e.g. pharmacy, changed
  // by a separate "change pharmacy to X" turn) moved on in the same baseline — only the
  // specific field whose own identity changed server-side (e.g. dosage, after the draft's
  // catalog match re-resolved to a different strength/drugId) should lose its edit. If this
  // card was just remounted (e.g. after navigating to another tab and back) editState
  // resets to {}, so fall back to the persisted ambient store (same per-field merge) before
  // the server baseline — otherwise a remount silently discards edits the clinician
  // already made.
  //
  // A field the server REJECTED (getRejectedFields — recorded from the result payload's
  // field_corrections) is dropped on top of that, because baseline movement cannot detect it:
  // a rejected edit comes back with the field exactly as it was, so the "did this field's
  // server value move?" test says no and the dead edit would keep overriding the card forever
  // — which is how a Capsule the catalog does not stock stayed on screen after the server had
  // already corrected the draft back to Tablet.
  const effectiveEdits = (p: Prescription): PrescriptionEdits => {
    const k = flagKeyOf(p);
    const baseline = defaultEdits(p);
    const capturedBaseline = editBaseline[k];
    const name = (p.medication_name || p.drugName || "").trim();
    const rejected = name ? getRejectedFields(patientId, name) : undefined;
    if (editState[k]) {
      return capturedBaseline
        ? mergeFreshBaselineWithStaleEdits(
            editState[k],
            capturedBaseline,
            baseline,
            rejected
          )
        : dropRejectedEdits(editState[k], baseline, rejected);
    }
    const persisted = name
      ? getPrescriptionEdits(patientId, name, baseline)
      : undefined;
    return persisted ?? baseline;
  };

  const handleEditChange = (
    p: Prescription,
    field: keyof PrescriptionEdits,
    value: string
  ) => {
    let nextValue = value;
    if (field === "refills") {
      if (nextValue === "") {
        nextValue = "0";
      } else {
        const num = parseInt(nextValue, 10);
        if (Number.isNaN(num)) return;
        nextValue = String(Math.max(0, num));
      }
    }

    const k = flagKeyOf(p);
    const baseline = defaultEdits(p);
    // Resume editing from exactly what the card is DISPLAYING (effectiveEdits — still-valid
    // edits kept, stale and server-rejected ones reset to fresh), never a second, separately
    // derived notion of the current values: re-deriving it here is how a field the clinician
    // could plainly see got silently replaced by something else the moment they touched a
    // different field.
    const cur = effectiveEdits(p);
    setEditBaseline((prev) => ({ ...prev, [k]: baseline }));
    setEditState((prev) => ({ ...prev, [k]: { ...cur, [field]: nextValue } }));
  };

  // Mirror genuine local edits into the ambient store so the Send/Save flows (and a
  // remounted card, see effectiveEdits above) can pick them up. Only mirror entries that
  // exist in local editState — i.e. the user actually touched that field this mount.
  // Mirroring effectiveEdits() unconditionally would write the server-baseline defaults
  // (including whatever preferredPharmacy happened to be on the very first render, before
  // it loads from the websocket) into the store, permanently shadowing later prop updates.
  useEffect(() => {
    prescriptions.forEach((p) => {
      const name = (p.medication_name || p.drugName || "").trim();
      if (!name) return;
      const k = flagKeyOf(p);
      if (!editState[k]) return;
      const baseline = editBaseline[k] ?? defaultEdits(p);
      setPrescriptionEdits(patientId, name, editState[k], baseline);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prescriptions, editState, editBaseline, patientId]);

  // Renders a read-only label + value row matching the summary card mockup.
  const renderDisplayField = (
    label: string,
    value: string
  ): React.ReactNode => (
    <Typography variant="body2" component="div" sx={{ lineHeight: 1.5 }}>
      <Box component="span" sx={FIELD_LABEL_SX}>
        {label}:{" "}
      </Box>
      <Box component="span" sx={FIELD_VALUE_SX}>
        {value || "—"}
      </Box>
    </Typography>
  );

  // Renders a bordered editable field beside its label (same row).
  const renderInlineEditField = (
    p: Prescription,
    field: keyof PrescriptionEdits,
    label: string,
    opts?: {
      multiline?: boolean;
      type?: string;
    }
  ): React.ReactNode => {
    const value = effectiveEdits(p)[field] ?? "";
    const isRefills = field === "refills";

    if (opts?.multiline) {
      // Label and textarea sit in a flex row so the value starts on the same
      // line as the label; wrapped lines then flow under the value column.
      // Autosize height after each change so wrapped SIG text is fully visible.
      const syncSigHeight = (el: HTMLTextAreaElement | null) => {
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
      };
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            flexWrap: "nowrap",
            gap: 0.75,
            lineHeight: 1.5,
            minWidth: 0,
            width: "100%",
          }}
        >
          <Typography
            variant="body2"
            component="span"
            sx={{ ...FIELD_LABEL_SX, pt: 0.5, flexShrink: 0 }}
          >
            {label}:
          </Typography>
          <TextField
            value={value}
            onChange={(e) => handleEditChange(p, field, e.target.value)}
            variant="standard"
            size="small"
            multiline
            minRows={1}
            InputProps={{ disableUnderline: true }}
            inputRef={(el: HTMLTextAreaElement | null) => syncSigHeight(el)}
            onInput={(e) =>
              syncSigHeight(e.target as HTMLTextAreaElement)
            }
            sx={{ ...SIG_INPUT_SX, flex: 1, minWidth: 0 }}
          />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "nowrap",
          gap: 0.75,
          lineHeight: 1.5,
          minWidth: 0,
          width: "100%",
        }}
      >
        <Typography
          variant="body2"
          component="span"
          sx={{ ...FIELD_LABEL_SX, flexShrink: 0 }}
        >
          {label}:
        </Typography>
        <TextField
          value={value}
          onChange={(e) => handleEditChange(p, field, e.target.value)}
          onKeyDown={
            isRefills
              ? (e) => {
                  if (e.key === "-" || e.key === "e" || e.key === "E") {
                    e.preventDefault();
                  }
                }
              : undefined
          }
          variant="standard"
          size="small"
          type={isRefills ? "number" : opts?.type}
          inputProps={isRefills ? { min: 0, step: 1 } : undefined}
          InputProps={{ disableUnderline: true }}
          sx={{
            ...(isRefills ? REFILLS_INPUT_SX : inlineTextFieldSx(value)),
            flex: "0 1 auto",
            minWidth: 0,
          }}
        />
      </Box>
    );
  };

  const renderSummaryField = (
    p: Prescription,
    field: keyof PrescriptionEdits,
    label: string,
    opts?: {
      multiline?: boolean;
      type?: string;
    }
  ): React.ReactNode => {
    if (p.sent || !isEditing(p)) {
      return renderDisplayField(label, effectiveEdits(p)[field] ?? "");
    }
    return renderInlineEditField(p, field, label, opts);
  };

  // Fields the clinician changed on the card, compared to the server baseline.
  const changedEditFields = (p: Prescription): Array<keyof PrescriptionEdits> => {
    const base = defaultEdits(p);
    const cur = effectiveEdits(p);
    return (Object.keys(cur) as Array<keyof PrescriptionEdits>).filter(
      (k) => (cur[k] ?? "").toString().trim() !== (base[k] ?? "").toString().trim()
    );
  };

  // Delimiter used to join per-field "label to value" clauses when composing the
  // update instruction sent to the backend LLM. A comma collides with legitimate
  // clinical text (e.g. a SIG containing "Take 1 tablet twice daily, with food"),
  // making the field boundary ambiguous. A pipe essentially never appears in
  // SIG/dosage/quantity text, so it's a much safer separator; on the rare chance a
  // value itself contains a pipe, escape it first so it can't be mistaken for a
  // field boundary.
  const FIELD_JOIN_DELIMITER = " | ";
  const escapeFieldValue = (value: string): string => value.replace(/\|/g, "\\|");

  // Build the chat message for the Send button. With no edits this is the plain
  // send phrase; with edits it is an update-then-send instruction so the edited
  // values are applied to the draft before it goes to the pharmacy.
  const buildEditedSendMessage = (p: Prescription): string => {
    const cur = effectiveEdits(p);
    const originalName = (p.medication_name || p.drugName || "").trim();
    const finalName = (cur.drugName || originalName).trim();
    const changed = changedEditFields(p);
    if (changed.length === 0) {
      return buildSendPrescriptionMessage(finalName);
    }
    const parts = changed.map(
      (k) =>
        `${FIELD_PHRASE[k]} to ${escapeFieldValue(
          (cur[k] ?? "").toString().trim() || "(none)"
        )}`
    );
    const updateInstruction = `update ${originalName || finalName}: ${parts.join(
      FIELD_JOIN_DELIMITER
    )}`;
    return `${updateInstruction}. Then send ${finalName} prescription to pharmacy`;
  };

  // Build the chat message for "Send all". Previously this was always the plain
  // SEND_ALL_PRESCRIPTIONS_MESSAGE literal, so edits made on any card (other than the
  // single-drug Send path) never reached the backend. Now it composes one update clause
  // per unsent drug that has changed fields, same phrasing as the single-drug Send, so
  // every edited draft is updated before the batch send.
  const buildSendAllMessage = (): string => {
    const updateClauses = prescriptions
      .filter((p) => !p.sent)
      .map((p) => {
        const changed = changedEditFields(p);
        if (changed.length === 0) return null;
        const cur = effectiveEdits(p);
        const originalName = (p.medication_name || p.drugName || "").trim();
        const finalName = (cur.drugName || originalName).trim();
        const parts = changed.map(
          (k) =>
            `${FIELD_PHRASE[k]} to ${escapeFieldValue(
              (cur[k] ?? "").toString().trim() || "(none)"
            )}`
        );
        return `update ${originalName || finalName}: ${parts.join(
          FIELD_JOIN_DELIMITER
        )}`;
      })
      .filter((clause): clause is string => !!clause);

    if (updateClauses.length === 0) {
      return SEND_ALL_PRESCRIPTIONS_MESSAGE;
    }
    return `${updateClauses.join(". ")}. Then send all prescriptions to pharmacy`;
  };

  // EPA / Priority Indicator / Coverage controls. ``direction`` controls whether they
  // sit on one line ("row", above pharmacy) or stack ("column", right column). The
  // markup is defined once here so switching FLAGS_LAYOUT needs no markup duplication.
  // Field rows stay in normal (block) text flow — NOT flex — so a wrapping value like the
  // SIG keeps its label at the top-left and preserves the space after the colon (flex
  // would center the label against the wrapped lines and collapse the whitespace).
  // Row spacing comes solely from the column `gap` (no min-height), so every row is
  // separated uniformly whether or not its value wraps. Rows are content-sized and the
  // checkbox rows carry no vertical padding, so both columns line up row-for-row.
  const renderFlagControls = (
    p: Prescription,
    direction: "row" | "column"
  ): React.ReactNode => {
    // EPA / Priority / Coverage: service-level epaEnabled plus a resolved session PBM.
    if (!showEpaPriority) return null;
    const isRow = direction === "row";
    const cov = coverageDisplay(p.coverage);
    const checkboxSx = {
      color: primaryColor,
      "&.Mui-checked": { color: primaryColor },
      py: 0,
      // Drop the checkbox's built-in left padding so its box aligns with the
      // left edge of the Route/Refills text above it (keep right padding for
      // the gap before the label). The small negative margin compensates for
      // the few px of internal inset in the MUI checkbox icon so the box
      // outline sits flush with the text.
      pl: 0,
      ml: "-2px",
    } as const;
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: direction,
          alignItems: isRow ? "center" : "flex-start",
          gap: isRow ? 3.5 : 1,
          flexWrap: isRow ? "wrap" : "nowrap",
        }}
      >
        {!p.sent && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={effectiveFlags(p).priorAuthorization}
                    onChange={(e) =>
                      handleFlagToggle(p, "priorAuthorization", e.target.checked)
                    }
                    sx={checkboxSx}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    EPA
                  </Typography>
                }
                sx={{ m: 0 }}
              />
              <Tooltip title="Electronic Prior Authorization">
                <InfoOutlinedIcon sx={{ color: primaryColor, fontSize: "16px" }} />
              </Tooltip>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={effectiveFlags(p).priorityIndicator}
                    onChange={(e) =>
                      handleFlagToggle(p, "priorityIndicator", e.target.checked)
                    }
                    sx={checkboxSx}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Priority Indicator
                  </Typography>
                }
                sx={{ m: 0 }}
              />
              <Tooltip title="Mark prescription as priority">
                <InfoOutlinedIcon sx={{ color: primaryColor, fontSize: "16px" }} />
              </Tooltip>
            </Box>
          </>
        )}
        {cov && (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#d32f2f" }}
          >
            <WarningAmberIcon sx={{ fontSize: "16px" }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#d32f2f" }}>
              {cov}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        variant="body1"
        sx={{
          color: "#212529",
          fontWeight: 600,
          marginBottom: "10px",
        }}
      >
        Summary:
      </Typography>
      <Stack direction="column" gap={2}>
        {prescriptions.map((prescription, index) => {
          return (
            <Card
              key={`${conversationId}-${prescription.id || index}`}
              sx={{
                mb: 0,
                border: "1px solid #ddd",
                borderRadius: "8px",
                bgcolor: "#ffffff",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Grid container rowSpacing={1.25} columnSpacing={2}>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      {/* Medication name is never editable, even in edit mode — it's the
                          catalog-resolved identity of the draft, not a clinician-adjustable
                          field like dose/form/SIG/quantity/refills below. */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            color: primaryColor,
                            fontWeight: 700,
                            fontSize: DRUG_NAME_FONT_SIZE,
                            lineHeight: 1.3,
                          }}
                        >
                          {effectiveEdits(prescription).drugName || "—"}
                        </Typography>
                        {prescription.scheduleDrugFlag === true &&
                          prescription.deaClassCode && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              mt: 0.25,
                              fontWeight: 600,
                              color: primaryColor,
                            }}
                          >
                            DEA {prescription.deaClassCode}
                          </Typography>
                        )}
                      </Box>
                      {!prescription.sent && (
                        <Tooltip
                          title={
                            isEditing(prescription)
                              ? "Done editing"
                              : "Edit prescription"
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => toggleEditMode(prescription)}
                            aria-label={
                              isEditing(prescription)
                                ? "Done editing"
                                : "Edit prescription"
                            }
                            sx={{
                              color: isEditing(prescription) ? "#fff" : primaryColor   ,
                              backgroundColor: isEditing(prescription) ? primaryColor : "#fff !important",
                              mt: 0.25,
                              flexShrink: 0,
                              "&:hover": {
                                backgroundColor: isEditing(prescription) ? primaryColor : "#fff !important",
                              },
                              "&:focus": {
                                backgroundColor: isEditing(prescription) ? primaryColor : "#fff !important",
                              },
                              "&:active": {
                                backgroundColor: isEditing(prescription) ? primaryColor : "#fff !important",
                              },
                              "&:focus-visible": {
                                backgroundColor: isEditing(prescription) ? primaryColor : "#fff !important",
                              },
                            }}
                          >
                            {isEditing(prescription) ? (
                              <CheckOutlinedIcon fontSize="small" />
                            ) : (
                              <EditOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Grid>
                  {/* Dose | Route */}
                  <Grid item xs={12} sm={6}>
                    {renderSummaryField(prescription, "dosage", "Dose")}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {renderDisplayField(
                      "Route",
                      effectiveEdits(prescription).route
                    )}
                  </Grid>

                  {/* Form | Refills */}
                  <Grid item xs={12} sm={6}>
                    {renderSummaryField(prescription, "form", "Form")}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {renderSummaryField(prescription, "refills", "Refills")}
                  </Grid>

                  {/* SIG — full width */}
                  <Grid item xs={12}>
                    {renderSummaryField(prescription, "sig", "SIG", {
                      multiline: true,
                    })}
                  </Grid>

                  {/* Quantity | EPA + Priority Indicator */}
                  <Grid item xs={12} sm={6}>
                    {renderSummaryField(prescription, "quantity", "Quantity")}
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    {renderFlagControls(prescription, "row")}
                  </Grid>

                  {/* Substitutions | EPCS (EPCS lines up under EPA) */}
                  <Grid item xs={12} sm={6}>
                    {renderDisplayField(
                      "Substitutions",
                      effectiveEdits(prescription).substitutions ||
                        DEFAULT_SUBSTITUTION
                    )}
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    {showEpcsCheckbox(prescription) && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={effectiveFlags(prescription).epcsQueued}
                              onChange={(e) =>
                                handleFlagToggle(
                                  prescription,
                                  "epcsQueued",
                                  e.target.checked
                                )
                              }
                              sx={{
                                color: primaryColor,
                                "&.Mui-checked": { color: primaryColor },
                                py: 0,
                                pl: 0,
                                ml: "-2px",
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              EPCS
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                        <Tooltip title="Please check only if you want to EPCS Queue for 2FA">
                          <InfoOutlinedIcon
                            sx={{ color: primaryColor, fontSize: "16px" }}
                          />
                        </Tooltip>
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ borderTop: "1px solid #eee", mt: 0.5 }} />
                  </Grid>
                  {(() => {
                    const edits = effectiveEdits(prescription);
                    const hasPharmacy = !!(
                      edits.pharmacy?.trim() ||
                      prescription.pharmacy?.trim() ||
                      preferredPharmacy?.trim() ||
                      isEditing(prescription)
                    );
                    const showSendButton = !!(
                      canSend &&
                      !prescription.sent &&
                      (prescription.medication_name || prescription.drugName)
                    );
                    if (!hasPharmacy && !showSendButton && !prescription.sent) {
                      return null;
                    }
                    return (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1.5,
                            flexWrap: "wrap",
                          }}
                        >
                          {prescription.sent ? (
                            hasPharmacy ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                }}
                              >
                                <LocalPharmacyIcon
                                  sx={{
                                    color: primaryColor,
                                    fontSize: "18px",
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                  }}
                                >
                                  Pharmacy:{" "}
                                  {prescription.pharmacy?.trim() || preferredPharmacy}
                                </Typography>
                              </Box>
                            ) : (
                              <Box />
                            )
                          ) : (
                            <Box
                              sx={{
                                flex: "1 1 260px",
                                minWidth: 220,
                                maxWidth: 460,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <LocalPharmacyIcon
                                sx={{
                                  color: primaryColor,
                                  fontSize: "18px",
                                  flexShrink: 0,
                                }}
                              />
                              {isEditing(prescription) ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "nowrap",
                                    gap: 0.75,
                                    minWidth: 0,
                                    flex: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    component="span"
                                    sx={{ ...FIELD_LABEL_SX, flexShrink: 0 }}
                                  >
                                    Pharmacy:
                                  </Typography>
                                  <TextField
                                    value={effectiveEdits(prescription).pharmacy}
                                    onChange={(e) =>
                                      handleEditChange(
                                        prescription,
                                        "pharmacy",
                                        e.target.value
                                      )
                                    }
                                    variant="standard"
                                    size="small"
                                    InputProps={{ disableUnderline: true }}
                                    sx={{
                                      ...INPUT_SIZE_RESET_SX,
                                      flex: 1,
                                      minWidth: 0,
                                      "& .MuiInputBase-root": {
                                        ...INPUT_SIZE_RESET_SX["& .MuiInputBase-root"],
                                        ...editableFieldRootSx,
                                        width: "100%",
                                        px: 0.75,
                                        py: 0.35,
                                      },
                                      "& .MuiInputBase-input": {
                                        ...INPUT_SIZE_RESET_SX["& .MuiInputBase-input"],
                                        padding: "1px 2px",
                                        lineHeight: 1.5,
                                      },
                                    }}
                                  />
                                </Box>
                              ) : (
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  <Box component="span" sx={FIELD_LABEL_SX}>
                                    Pharmacy:{" "}
                                  </Box>
                                  <Box component="span" sx={FIELD_VALUE_SX}>
                                    {effectiveEdits(prescription).pharmacy || "—"}
                                  </Box>
                                </Typography>
                              )}
                            </Box>
                          )}
                          {prescription.sent ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                color: "#2e7d32",
                                fontWeight: 600,
                                fontSize: "0.8125rem",
                              }}
                            >
                              <SendIcon sx={{ fontSize: "16px" }} />
                              Sent to pharmacy
                            </Box>
                          ) : showSendButton ? (
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<SendIcon />}
                              disabled={disabled}
                              onClick={() => {
                                console.log("[SendButton] Clicked, disabled:", disabled);
                                if (disabled) return;
                                try {
                                  const msg = buildEditedSendMessage(prescription);
                                  console.log("[SendButton] Built message:", msg);
                                  markSendOriginButton(msg);
                                  console.log("[SendButton] Marked origin, calling onSendPrescription");
                                  onSendPrescription?.(msg);
                                } catch (err) {
                                  console.error("[SendButton] Error:", err);
                                }
                              }}
                              sx={{ textTransform: "none", fontWeight: 600 }}
                            >
                              Send
                            </Button>
                          ) : null}
                        </Box>
                      </Grid>
                    );
                  })()}
                </Grid>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
      {showSendAll ? (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            disabled={disabled}
            onClick={() => {
              console.log("[SendAllButton] Clicked, disabled:", disabled);
              if (disabled) return;
              try {
                const msg = buildSendAllMessage();
                console.log("[SendAllButton] Built message:", msg);
                markSendOriginButton(msg);
                console.log("[SendAllButton] Marked origin, calling onSendPrescription");
                onSendPrescription?.(msg);
              } catch (err) {
                console.error("[SendAllButton] Error:", err);
              }
            }}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Send all
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

export default PrescriptionSummary;

